import * as React from 'react';
import { gql } from '@apollo/client';
import { LinkTrackingController, LinkTrackingControllerProps } from './LinkTracking.controller';
import {
  GetBitlyAccountsDocument,
  useConnectBitlyMutation,
  useGetBitlyAccountsLazyQuery,
  useReconnectBitlyMutation,
  useRemoveBitlyMutation,
  useStartSocialOAuthMutation,
  useUpdateCustomBitlySettingsMutation,
  useUpdateWorkspaceSettingsMutation
} from '../../../models/graphql';
import { useSelectedOrganization } from '../../../hooks/useSelectedOrganization';

gql`
  query GetBitlyAccounts($workspaceId: ID!) {
    workspace(workspaceId: $workspaceId) {
      id
      settings {
        id
        campaignTracking
        shouldRemoveUtm
        utm {
          source
          medium
          campaign
          term
          content
        }
        defaultBitly {
          domain
          bitly {
            ...CoreBitlyFields
          }
        }
      }
      bitlyAccounts {
        ...CoreBitlyFields
      }
      socialProfiles {
        ...SocialProfileFields
        customBitly {
          ...CoreCustomBitlyFields
          bitly {
            ...CoreBitlyFields
          }
        }
      }
    }
  }

  mutation ConnectBitly($workspaceId: ID!, $socialAuthToken: String!) {
    connectBitly(workspaceId: $workspaceId, socialAuthToken: $socialAuthToken) {
      ...CoreBitlyFields
    }
  }

  mutation ReconnectBitly($bitlyId: ID!, $socialAuthToken: String!) {
    reconnectBitly(bitlyId: $bitlyId, socialAuthToken: $socialAuthToken) {
      ...CoreBitlyFields
    }
  }

  mutation RemoveBitly($bitlyId: ID!) {
    removeBitly(bitlyId: $bitlyId)
  }

  mutation UpdateCustomBitlySettings($input: [UpdateCustomBitlySettingsInput!]!) {
    bulkUpdateCustomBitlySettings(input: $input) {
      id
      customBitly {
        ...CoreCustomBitlyFields
        bitly {
          ...CoreBitlyFields
        }
      }
    }
  }
`;

export const LinkTrackingApollo = (): JSX.Element => {
  const { selectedWorkspace } = useSelectedOrganization();

  const [startSocialOAuth] = useStartSocialOAuthMutation({
    fetchPolicy: 'no-cache'
  });

  const [getBitlyAccounts, bitlyResults] = useGetBitlyAccountsLazyQuery();

  const [connectBitlyAccount] = useConnectBitlyMutation({
    refetchQueries: [
      {
        query: GetBitlyAccountsDocument,
        variables: { workspaceId: selectedWorkspace?.id }
      }
    ]
  });

  const [reconnectBitlyAccount] = useReconnectBitlyMutation();
  const [removeBitlyAccount] = useRemoveBitlyMutation();

  const [updateWorkspaceSettings] = useUpdateWorkspaceSettingsMutation();

  const [updateCustomBitlySettings] = useUpdateCustomBitlySettingsMutation();

  const controllerProps: LinkTrackingControllerProps = {
    getBitlyAccounts,
    bitlyResults,
    startSocialOAuth,
    connectBitlyAccount,
    reconnectBitlyAccount,
    removeBitlyAccount,
    updateWorkspaceSettings,
    updateCustomBitlySettings
  };

  return <LinkTrackingController {...controllerProps} />;
};
